import { Icon, IconButton, Skeleton, TextInput, Typography } from "@/components/atoms";
import { useNavigate } from "react-router-dom";
import { PricingMethodCard } from "../../common/PricingMethodCard";
import { useForm } from "react-hook-form";
import { useAddPricingProfileMutation } from "@/redux/apis/config/trip/tripCost";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast } from "@/utils";
import { CompaniesAttachedTable } from "./CompaniesAttachedTable";

export const PricingProfileAddPanel = () => {
  const navigate = useNavigate();

  const [addPricingProfile, { isLoading }] = useAddPricingProfileMutation();
  const {
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<{ name: string }>({
    defaultValues: { name: "" },
  });

  const onSubmit = handleSubmit((data) => {
    if (isDirty) {
      addPricingProfile({ name: data.name })
        .unwrap()
        .then((data) => {
          addToast("success", "Successfully Added New Pricing Profile");
          navigate(`../${data.uuid}`, { replace: true });
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    }
  });

  if (isLoading) return <Skeleton />;

  return (
    <div className="flex flex-col">
      <form className="flex flex-row items-center gap-x-1.5" onSubmit={onSubmit}>
        <TextInput autoFocus size="md" placeholder="Enter Pricing Profile Name" className="flex-1" maxLength={65} required {...register("name")} />
        <IconButton variant="custom" iconName="CloseCircle" iconSize="lg" iconVariant="Bold" className="text-neutral-dark-gray" onClick={() => navigate("..")} />
        <IconButton type="submit" variant="custom" iconName="TickCircle" iconSize="lg" iconVariant="Bold" className="text-success" />
      </form>
      <div className="flex flex-col opacity-25">
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">For Contract Pricing Profiles, RideMinder applies pricing options in this order (1 → 2)</Typography>
        <Typography variant="paragraph" className="mt-1">
          If neither is configured, the system will use 
          <span className="text-info cursor-pointer" onClick={() => navigate("../../custom-rates")}> Custom Rates </span> 
          first, then fall back to the 
          <span className="text-info cursor-pointer" onClick={() => navigate("../../default-rates")}> Default Rates</span>
        </Typography>
      </div>
      <div className="flex flex-col md:flex-row gap-5 mt-6 opacity-25">
        <PricingMethodCard
          title="Zone Rates" 
          description="Set prices for specific areas like airports, business districts or special operating hubs"
          icon={<Typography variant="h1" className="text-neutral-dark-gray">1</Typography>}
          onConfigure={() => navigate("zone-rates")}
          warning="Pricing not set"
        >
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row items-center rounded-lg bg-neutral-surface-gray gap-2 px-2 py-1.5 text-neutral-dark-gray">
              <Icon name="InfoCircle" size="lg" className="flex-shrink-0" />
              <Typography variant="paragraph">This pricing applies when a pickup falls within your defined zones</Typography>
            </div>
          </div>
        </PricingMethodCard>
        <PricingMethodCard
          title="State Rates" 
          description="Set prices for an entire state."
          icon={<Typography variant="h1" className="text-neutral-dark-gray">2</Typography>}
          onConfigure={() => navigate("state-rates")}
          warning="Pricing not set"
        >
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row items-center rounded-lg bg-neutral-surface-gray gap-2 px-2 py-1.5 text-neutral-dark-gray">
              <Icon name="InfoCircle" size="lg" className="flex-shrink-0" />
              <Typography variant="paragraph">This pricing applies when the pickup falls within the state</Typography>
            </div>
          </div>
        </PricingMethodCard>
      </div>
      <CompaniesAttachedTable companies={[]} className="mt-8 opacity-25" />
    </div>
  );
};